import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { HeaderNav } from "@/components/HeaderNav";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { User, Building2, ShieldCheck } from "lucide-react";

export default function Consent() {
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/me/assessment-access")
      .then((r) => { if (!r.data.has_access) navigate("/payment", { replace: true }); })
      .catch(() => { /* non-blocking */ });
  }, [navigate]);

  const proceed = async () => {
    if (!agreed) return;
    setBusy(true);
    try {
      await api.post("/assessment/consent", { agreed: true });
      await api.post("/assessment/start");
      navigate("/assessment");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      if (err.response?.status === 402) navigate("/payment", { replace: true });
    } finally { setBusy(false); }
  };

  return (
    <div>
      <HeaderNav />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-14 pb-20 cg-fade-up">
        <p className="cg-eyebrow mb-3">Privacy &amp; Consent</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#F3F4F6] mb-4">Before you begin</h1>
        <p className="text-base text-[#9CA3AF] leading-relaxed max-w-2xl">
          Caesar's Ghost is a reflective assessment, not a test you pass or fail. Please read how your
          data is handled. This distinction is important and we do not bury it in terms and conditions.
        </p>

        <div className="mt-6 border border-brass/40 bg-[rgba(217,162,95,0.08)] rounded-xl p-4" data-testid="consent-notice">
          <p className="text-sm text-[#E8EAEE] leading-relaxed">
            <span className="font-medium text-brass">For personal reflection.</span>{" "}
            Caesar's Ghost is a reflective assessment. Your responses support your personal Authority Portrait
            and, where relevant, combined group insights. Results are for personal reflection only. They are
            not a clinical, diagnostic, or psychometric evaluation and must not be used for hiring, ranking,
            or any employment decision.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <div className="cg-card p-6" data-testid="consent-you-receive">
            <User className="w-6 h-6 text-brass mb-3" />
            <h2 className="font-serif text-2xl text-[#F3F4F6] mb-3">What you receive</h2>
            <ul className="space-y-2 text-sm text-[#9CA3AF] leading-relaxed list-disc pl-4">
              <li>Your private, personal Authority Portrait across seven dimensions.</li>
              <li><span className="text-[#F3F4F6]">Ground</span>: what seems to help you make your own choices.</li>
              <li><span className="text-[#F3F4F6]">Ghosts</span>: influences or habits that may be worth a closer look.</li>
              <li><span className="text-[#F3F4F6]">Shifts</span>: situations where your authority might change.</li>
              <li><span className="text-[#F3F4F6]">Signals</span>: feelings, hunches and other things you may want to notice when making a choice.</li>
              <li><span className="text-[#F3F4F6]">Reclamation</span>: practical ways to make choices more consciously.</li>
              <li>Questions to think about, small things you can try, and C³, a simple way to apply your results to one real decision.</li>
              <li>A downloadable PDF report — yours to keep.</li>
            </ul>
          </div>
          <div className="cg-card p-6" data-testid="consent-org-receives">
            <Building2 className="w-6 h-6 text-ghostslate mb-3" />
            <h2 className="font-serif text-2xl text-[#F3F4F6] mb-3">What your organization or group receives</h2>
            <ul className="space-y-2 text-sm text-[#9CA3AF] leading-relaxed list-disc pl-4">
              <li>Your organization or group will <span className="text-[#F3F4F6]">not</span> receive your name, email address, individual answers or personal Authority Portrait.</li>
              <li>It will only receive combined results from participants when there are enough responses to protect individual privacy.</li>
              <li>Your organization will only see results for groups that are large enough to protect individual privacy. If a group is too small, its results will not be shown separately.</li>
            </ul>
          </div>
        </div>

        <div className="cg-card p-6 mt-5" data-testid="consent-data-handling">
          <ShieldCheck className="w-5 h-5 text-brass mb-3" />
          <p className="text-sm text-[#F3F4F6] mb-2 font-medium">How we handle your information</p>
          <p className="text-sm text-[#9CA3AF] leading-relaxed">
            Your responses and personal information are stored securely. We use your responses to create your
            Authority Portrait and, when you are participating through an organization or group, to contribute to
            combined group results. Your personal Authority Portrait and individual answers are not shared with the
            organization or group that invited you. You may request access to, export of, or deletion of your
            personal information, subject to applicable requirements.
          </p>
        </div>

        <label className="mt-8 flex items-start gap-3 cursor-pointer" data-testid="consent-checkbox-label">
          <input type="checkbox" data-testid="consent-checkbox" checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-1 w-5 h-5 accent-[#D9A25F]" />
          <span className="text-sm text-[#9CA3AF]">
            I understand what I receive and what my organization receives, and I consent to participate.
          </span>
        </label>

        <button data-testid="consent-continue-button" disabled={!agreed || busy} onClick={proceed}
          className="mt-8 bg-brass text-obsidian font-medium px-8 py-3.5 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? "Preparing…" : "I consent — begin assessment"}
        </button>
      </div>
    </div>
  );
}
